import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import { skip } from 'rxjs/operators';
import ReactPlayer from 'react-player';

import { MediaEntry } from 'src/types/global-types';
import { usePhotoswipeForGallery, useProductGallery } from 'src/hooks/useProductGallery';
import { useHistoryLocationObservable } from 'src/hooks/router-hooks';

import { Svg } from '../Svg/Svg';
import { Button } from '../Button/Button';

import classes from './ProductGallery.scss';

interface ProductGalleryProps {
    name: string;
}

export const ProductGalleryContext = React.createContext<{
    media_entries: MediaEntry[];
    setMediaEntries: (entries: MediaEntry[]) => void;
    setPending: () => void;
    pending: boolean;
}>({
    media_entries: [],
    setMediaEntries: (_entries: MediaEntry[]) => {
        console.warn('setMediaEntries not implemented');
    },
    setPending: () => {
        console.warn('setPending not implemented');
    },
    pending: false,
});

export const ProductGalleryProvider: React.FC<{ media_entries: MediaEntry[] }> = React.memo((props) => {
    const location$ = useHistoryLocationObservable();

    const [{ pending, media_entries }, setState] = useState({
        pending: false,
        media_entries: props.media_entries,
    });

    const setMediaEntries = useCallback((entries: MediaEntry[]) => {
        setState({
            pending: false,
            media_entries: entries,
        });
    }, []);

    const setPending = useCallback(() => {
        setState((prev) => {
            return {
                ...prev,
                pending: true,
            };
        });
    }, []);

    useEffect(() => {
        setMediaEntries(props.media_entries);
    }, [props.media_entries, setMediaEntries]);

    useEffect(() => {
        const sub = location$.pipe(skip(1)).subscribe(() => setPending());
        return () => sub.unsubscribe();
    }, [location$, setPending]);

    return (
        <ProductGalleryContext.Provider value={{ media_entries, pending, setMediaEntries, setPending }}>
            {props.children}
        </ProductGalleryContext.Provider>
    );
});

export function ProductGallery(props: ProductGalleryProps) {
    const { name } = props;
    const [slideIndex, setSlideIndex] = useState(0);
    const [updateCount, setUpdateCount] = useState(0);
    const { media_entries } = useContext(ProductGalleryContext);
    const totalLength = media_entries.length;
    const sliderRef = useRef<Slider>(null);
    /**
     * Product gallery state
     */
    const { sortedMediaEntries, selectedIndex, hasThumbs, thumbsRef } = useProductGallery(media_entries);

    /**
     * Photoswipe state
     */
    const { showPhotoswipe, photoswipeReady, photoSwipeRef, photoswipeHtml } = usePhotoswipeForGallery({
        selectedIndex,
        sortedMediaEntries,
    });

    /**
     * Next arrow component
     * Slick slider does not provide type definitions for the props
     */
    function NextArrow(props) {
        const { className, onClick } = props as { className: string; onClick: () => void };
        const disabled = className.indexOf('slick-disabled') > -1;

        return (
            <Button
                disabled={disabled}
                onClick={onClick}
                variant="reset"
                className={classes.galleryNext}
                aria-label="Click to go to the next slide"
            >
                <Svg name="arrow-right" />
            </Button>
        );
    }

    /**
     * Prev arrow component
     * Slick slider does not provide type definitions for the props
     */
    function PrevArrow(props) {
        const { className, onClick } = props as { className: string; onClick: () => void };
        const disabled = className.indexOf('slick-disabled') > -1;

        return (
            <Button
                disabled={disabled}
                onClick={onClick}
                variant="reset"
                className={classes.galleryPrev}
                aria-label="Click to go to the previous slide"
            >
                <Svg name="arrow-left" />
            </Button>
        );
    }

    /**
     * Gallery setting for slick slider
     */
    const settings: Settings = {
        accessibility: true,
        arrows: true,
        autoplay: false,
        className: classes.gallerySlider,
        dots: false,
        draggable: true,
        fade: true,
        infinite: false,
        slidesToScroll: 1,
        slidesToShow: 1,
        speed: 500,
        lazyLoad: 'ondemand',
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        afterChange: () => setUpdateCount(updateCount + 1),
        beforeChange: (_current, next: number) => setSlideIndex(next),
    };

    return (
        <>
            {photoswipeReady && <div dangerouslySetInnerHTML={{ __html: photoswipeHtml }} ref={photoSwipeRef} />}

            <section className={classes.gallery}>
                <div className={classes.galleryViewPort}>
                    <div className={classes.gallerySpacer} />
                    <Slider ref={sliderRef} {...settings}>
                        {sortedMediaEntries.map((item, index) => {
                            const isVideo = item?.video_content?.video_url !== undefined;
                            const videoUrl = isVideo && item?.video_content?.video_url;
                            const isImage = !isVideo && item;
                            const title = item?.video_content?.video_title ?? '';

                            return (
                                <div key={index} className={classes.gallerySliderItem}>
                                    {totalLength > 0 && !isVideo && (
                                        <button
                                            aria-label="Zoom"
                                            className={classes.galleryZoom}
                                            onClick={showPhotoswipe}
                                            type="button"
                                        >
                                            <Svg name="zoom" />
                                        </button>
                                    )}

                                    {isVideo && videoUrl && (
                                        <ReactPlayer
                                            url={videoUrl}
                                            title={title}
                                            controls={true}
                                            className={classes.galleryVideo}
                                            width="100%"
                                            height="100%"
                                            config={{
                                                youtube: {
                                                    playerVars: {
                                                        autoplay: 1,
                                                        rel: 0,
                                                    },
                                                },
                                            }}
                                        />
                                    )}

                                    {isImage && (
                                        <img className={classes.galleryImage} src={item.medium_url} alt={name} />
                                    )}
                                </div>
                            );
                        })}
                    </Slider>
                </div>

                {hasThumbs && (
                    <div className={classes.galleryThumbs}>
                        <ul className={classes.galleryThumbsList} ref={thumbsRef}>
                            {sortedMediaEntries
                                .filter((item) => !item.hidden)
                                .map((item, index) => {
                                    const isSelected = index === slideIndex;
                                    const isVideoThumb =
                                        item.video_content?.video_url !== undefined &&
                                        item.video_content?.video_url !== '';

                                    return (
                                        <li
                                            data-thumb-item-selected={String(isSelected)}
                                            data-ui-video={isVideoThumb}
                                            className={classes.galleryThumbItem}
                                            onClick={() => sliderRef?.current?.slickGoTo(index)}
                                            key={`${index}-${item.small_url}`}
                                        >
                                            {isVideoThumb && <Svg name="play" />}
                                            <img alt={item.label} data-src={item.small_url} />
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                )}
            </section>
        </>
    );
}
